import moment from 'moment';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { GroupsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	Container,
	Empty,
	Jumbo,
	Pagination,
	Select,
	Separator,
	Skeleton,
	Table,
	tokens,
} from '@treams/treams-ui';

const Background = styled(Box)`
	min-height: 100vh;
	background: ${tokens.surface.secondary};
`;

const StyledLink = styled(NavLink)`
	${({ theme }) => css`
		color: ${theme.colors.primary};
		text-decoration: underline;

		&:hover {
			text-decoration: none;
		}
	`}
`;

export default function WorkspaceJobPositions() {
	const { t } = useTranslation();

	const { localize } = useLocalize();

	const [status, setStatus] = useState(graphql.UserStatus.Active);
	const [pagination, setPagination] = useState<Pagination>({ first: 10 });

	const query = graphql.useJobPositionsSettingsQuery({
		variables: { ...pagination, status },
	});

	const [archive] = graphql.useArchiveJobPositionMutation();

	return (
		<>
			<Jumbo
				title={t('settings.jobPositions.title')}
				description={t('settings.jobPositions.description')}
			/>
			<Separator />
			<Background>
				<Container py={4}>
					<Flex mb={3}>
						<Select
							search={false}
							onChange={(status) => {
								setStatus(status as graphql.UserStatus);
								setPagination({ first: 10 });
							}}
							selectedValue={status}
							options={[
								{
									title: t('settings.jobPositions.table.filter.userStatus.active'),
									value: graphql.UserStatus.Active,
								},
								{
									title: t('settings.jobPositions.table.filter.userStatus.inactive'),
									value: graphql.UserStatus.Inactive,
								},
								{
									title: t('settings.jobPositions.table.filter.userStatus.archived'),
									value: graphql.UserStatus.Archived,
								},
							]}
						/>
					</Flex>

					<Table>
						<Table.Header>
							<Table.Cell basis={0.2}>{t('settings.jobPositions.table.header.name')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.jobPositions.table.header.title')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.jobPositions.table.header.date')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.jobPositions.table.header.hris')}</Table.Cell>
							<Table.Cell basis={0.2}>{t('settings.jobPositions.table.header.actions')}</Table.Cell>
						</Table.Header>
						{query.data ? (
							query.data.users.edges.length === 0 ? (
								<Skeleton.Empty
									contents={
										<Empty icon={<GroupsIcon />} title={t('recipients.selector.noResults')} />
									}
								>
									<Skeleton.Repeat count={3}>
										<Table.Row>
											<Table.Cell.Skeleton basis={0.2} />
											<Table.Cell.Skeleton basis={0.2} />
											<Table.Cell.Skeleton basis={0.2} />
											<Table.Cell.Skeleton basis={0.2} />
											<Table.Cell.Skeleton basis={0.2} />
										</Table.Row>
									</Skeleton.Repeat>
								</Skeleton.Empty>
							) : (
								query.data.users.edges.map(({ node }) => (
									<Fragment key={node.id}>
										{node.jobPositions.length === 0 && (
											<Table.Row>
												<Table.Cell basis={0.2}>
													<Flex alignItems="center">
														<Box mr={2} flexGrow={0} flexShrink={0}>
															<Avatar user={node as graphql.User} size={24} />
														</Box>
														<StyledLink to={`employees/${node.id}`}>
															{node.firstName} {node.lastName}
														</StyledLink>
													</Flex>
												</Table.Cell>
												<Table.Cell basis={0.2}>–</Table.Cell>
												<Table.Cell basis={0.2}>–</Table.Cell>
												<Table.Cell basis={0.2}>–</Table.Cell>
												<Table.Cell basis={0.2}>
													<Button small type="destructive" title={t('common.archive')} disabled />
												</Table.Cell>
											</Table.Row>
										)}
										{node.jobPositions.map((job, i) => (
											<Table.Row key={job.id}>
												<Table.Cell basis={0.2}>
													{i === 0 && (
														<Flex alignItems="center">
															<Box mr={2} flexGrow={0} flexShrink={0}>
																<Avatar user={node as graphql.User} size={24} />
															</Box>
															<StyledLink to={`employees/${node.id}`}>
																{node.firstName} {node.lastName}
															</StyledLink>
														</Flex>
													)}
												</Table.Cell>
												<Table.Cell basis={0.2}>{localize(job.jobTitle.title).text}</Table.Cell>
												<Table.Cell basis={0.2}>
													{job.startDate
														? moment(job.startDate).format('L')
														: t('settings.employees.jobPositions.job.startDate.unknown')}
													{' – '}
													{job.endDate
														? moment(job.endDate).format('L')
														: t('settings.employees.jobPositions.job.endDate.unknown')}
												</Table.Cell>
												<Table.Cell basis={0.2}>{job.externalReferenceID}</Table.Cell>
												<Table.Cell basis={0.2}>
													<Button
														small
														type="destructive"
														title={t('common.archive')}
														onClick={async () => {
															await archive({ variables: { input: { id: job.id } } });
															await query.refetch();
														}}
													/>
												</Table.Cell>
											</Table.Row>
										))}
									</Fragment>
								))
							)
						) : (
							<Skeleton.Repeat count={3}>
								<Table.Row>
									<Table.Cell.Skeleton basis={0.25} />
									<Table.Cell.Skeleton basis={0.25} />
									<Table.Cell.Skeleton basis={0.3} />
									<Table.Cell.Skeleton basis={0.2} />
								</Table.Row>
							</Skeleton.Repeat>
						)}
					</Table>
					<Table.Paging
						pageInfo={query.data?.users.pageInfo}
						onPaginate={(pagination) => setPagination(pagination)}
					/>
				</Container>
			</Background>
		</>
	);
}
